<template>
  <section ref="rootEl" class="evpn-status-section">
    <header class="evpn-status-header">
      <div>
        <h4 class="evpn-status-title">BGP Peer Status</h4>
        <p class="evpn-status-target">Target: {{ target || "N/A" }}</p>
      </div>
      <button
        type="button"
        class="auto-refresh-toggle"
        :class="autoRefreshEnabled ? 'is-on' : 'is-off'"
        :disabled="loading"
        @click="toggleAutoRefresh"
      >
        <span class="auto-refresh-track">
          <span class="auto-refresh-knob"></span>
        </span>
        <span class="auto-refresh-label">Auto Refresh: {{ autoRefreshEnabled ? "ON" : "OFF" }}</span>
      </button>
    </header>

    <div v-if="errorMessage" class="alert alert-danger evpn-alert">{{ errorMessage }}</div>

    <div class="table-responsive">
      <table class="table table-striped table-bordered table-condensed evpn-status-table">
        <thead>
          <tr>
            <th>Peer Address</th>
            <th>Hostname</th>
            <th class="cell-text-right">Local ASN</th>
            <th class="cell-text-right">Peer ASN</th>
            <th class="cell-text-center">State</th>
            <th>Up/Down</th>
            <th class="cell-text-right">Received/Accepted</th>
            <th>Last Error</th>
            <th>BGPD</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!rows.length && !loading">
            <td colspan="9" class="text-center">No peers found.</td>
          </tr>
          <tr v-for="row in rows" :key="row.peer_address">
            <td>
              <a v-if="row.detail_url" :href="row.detail_url">{{ row.peer_address }}</a>
              <span v-else>{{ row.peer_address }}</span>
            </td>
            <td>{{ row.peer_host_name || '-' }}</td>
            <td class="cell-text-right">{{ row.local_as }}</td>
            <td class="cell-text-right">{{ row.peer_as }}</td>
            <td class="cell-text-center">
              <span :class="stateChipClass(row.state)">{{ row.state }}</span>
            </td>
            <td>
              {{ row.up_down_elapsed || '-' }}
            </td>
            <td class="cell-text-right">{{ formatNumber(row.received) }}/{{ formatNumber(row.accepted) }}</td>
            <td>{{ row.last_error || '-' }}</td>
            <td>{{ row.bgpd || '-' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from "vue"

const props = defineProps({
  dataUrl: {
    type: String,
    required: true
  }
})

const rows = ref([])
const target = ref("")
const loading = ref(false)
const errorMessage = ref("")
const autoRefreshEnabled = ref(false)
const rootEl = ref(null)
const hasStartedInitialLoad = ref(false)
let refreshTimer = null
let visibilityObserver = null

function formatNumber(value) {
  return Number(value || 0).toLocaleString()
}

function stateChipClass(state) {
  const normalized = String(state || '').toUpperCase()
  if (normalized === 'ESTABLISHED') return 'evpn-state-chip is-established'
  if (normalized === 'ACTIVE' || normalized === 'CONNECT' || normalized === 'OPENSENT' || normalized === 'OPENCONFIRM') {
    return 'evpn-state-chip is-connecting'
  }
  if (normalized === 'IDLE') return 'evpn-state-chip is-idle'
  return 'evpn-state-chip is-unknown'
}

async function loadRows() {
  loading.value = true
  errorMessage.value = ""
  try {
    const response = await fetch(props.dataUrl, {
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      credentials: "same-origin"
    })

    const payload = await response.json().catch(() => ({}))
    if (!response.ok) {
      throw new Error(payload?.message || `Request failed with status ${response.status}`)
    }

    target.value = payload?.target || ""
    rows.value = Array.isArray(payload?.rows) ? payload.rows : []
  } catch (error) {
    errorMessage.value = error?.message || "Failed to load EVPN peer status."
  } finally {
    loading.value = false
  }
}

function stopAutoRefresh() {
  if (refreshTimer) {
    window.clearInterval(refreshTimer)
    refreshTimer = null
  }
}

function startAutoRefresh() {
  stopAutoRefresh()
  refreshTimer = window.setInterval(() => {
    if (!autoRefreshEnabled.value || document.visibilityState !== "visible" || loading.value) return
    loadRows()
  }, 5000)
}

function toggleAutoRefresh() {
  autoRefreshEnabled.value = !autoRefreshEnabled.value
  if (autoRefreshEnabled.value) {
    ensureInitialLoad()
    startAutoRefresh()
    if (!loading.value) loadRows()
    return
  }
  stopAutoRefresh()
}

function ensureInitialLoad() {
  if (hasStartedInitialLoad.value) return
  hasStartedInitialLoad.value = true
  loadRows()
}

onMounted(() => {
  if (typeof window !== "undefined" && "IntersectionObserver" in window && rootEl.value) {
    visibilityObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return
      ensureInitialLoad()
      visibilityObserver?.disconnect()
      visibilityObserver = null
    }, { rootMargin: "200px 0px" })
    visibilityObserver.observe(rootEl.value)
  } else {
    ensureInitialLoad()
  }
})

onBeforeUnmount(() => {
  visibilityObserver?.disconnect()
  visibilityObserver = null
  stopAutoRefresh()
})
</script>

<style scoped>
.evpn-status-section {
  position: relative;
}

.evpn-status-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.evpn-status-title {
  margin: 0 0 4px;
}

.evpn-status-target {
  margin: 0;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
}

.evpn-alert {
  margin-bottom: 12px;
}

.evpn-status-table {
  margin-bottom: 0;
}

.cell-text-center {
  text-align: center;
  white-space: nowrap;
}

.cell-text-right {
  text-align: right;
  white-space: nowrap;
}

.evpn-state-chip {
  display: inline-block;
  min-width: 92px;
  text-align: center;
  font-weight: 700;
  font-size: 12px;
  border-radius: 999px;
  padding: 3px 10px;
}

.evpn-state-chip.is-established {
  color: #166534;
  background: #dcfce7;
}

.evpn-state-chip.is-connecting {
  color: #92400e;
  background: #fef3c7;
}

.evpn-state-chip.is-idle {
  color: #991b1b;
  background: #fee2e2;
}

.evpn-state-chip.is-unknown {
  color: #334155;
  background: #e2e8f0;
}

.auto-refresh-toggle {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border: 1px solid transparent;
  border-radius: 999px;
  padding: 6px 10px;
  font-weight: 700;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.auto-refresh-toggle.is-on {
  color: #14532d;
  background: #dcfce7;
  border-color: #86efac;
}

.auto-refresh-toggle.is-off {
  color: #7f1d1d;
  background: #fee2e2;
  border-color: #fca5a5;
}

.auto-refresh-track {
  position: relative;
  width: 38px;
  height: 22px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.2);
}

.auto-refresh-toggle.is-on .auto-refresh-track {
  background: #22c55e;
}

.auto-refresh-toggle.is-off .auto-refresh-track {
  background: #ef4444;
}

.auto-refresh-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: #fff;
  transition: transform 0.15s ease;
}

.auto-refresh-toggle.is-on .auto-refresh-knob {
  transform: translateX(16px);
}

.auto-refresh-label {
  white-space: nowrap;
}

</style>
